import { useQuery } from "@tanstack/react-query";
import { Database, HardDrive, Library } from "lucide-react";
import { getStats } from "../lib/api";
import SourceBadge from "./SourceBadge";

export default function StatsPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["stats"],
    queryFn: getStats,
    refetchInterval: 30_000,
  });

  if (isLoading) return <div className="skeleton h-48 w-full" />;
  if (error) return <div className="surface p-4 text-sm text-danger">{(error as Error).message}</div>;
  if (!data) return null;

  const cardsCount = "count" in data.cards ? data.cards.count : null;
  const decksCount = "count" in data.decks ? data.decks.count : null;
  const savedDecksCount = "count" in data.saved_decks ? data.saved_decks.count : null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          icon={Database}
          label="Standard cards"
          value={cardsCount != null ? cardsCount.toLocaleString() : "—"}
          sub={"count" in data.cards ? "DuckDB" : ("error" in data.cards ? data.cards.error : "")}
        />
        <StatCard
          icon={Library}
          label="Ingested decks"
          value={decksCount != null ? decksCount.toLocaleString() : "—"}
          sub={"count" in data.decks ? "Exemplar corpus" : ("error" in data.decks ? data.decks.error : "")}
        />
        <StatCard
          icon={HardDrive}
          label="Saved decks"
          value={savedDecksCount != null ? savedDecksCount.toLocaleString() : "—"}
          sub={"count" in data.saved_decks ? "All users" : data.saved_decks.error}
        />
      </div>

      {"by_source" in data.decks && data.decks.by_source.length > 0 && (
        <section className="surface p-4">
          <div className="text-[11px] uppercase tracking-wider text-fg-subtle mb-3">
            Per-source breakdown
          </div>
          <div className="space-y-2">
            {data.decks.by_source.map((row) => (
              <div
                key={row.source}
                className="grid grid-cols-[8rem_1fr_auto] items-center gap-3 text-sm"
              >
                <SourceBadge source={row.source} />
                <div className="font-mono tabular-nums text-fg-muted">
                  {row.count.toLocaleString()}
                </div>
                <div className="text-[11px] text-fg-subtle font-mono tabular-nums">
                  {row.max_created_at ? row.max_created_at.slice(0, 10) : "—"}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="surface p-4 text-xs text-fg-subtle font-mono space-y-1">
        <div>
          duckdb: <span className={data.files.duckdb_exists ? "text-success" : "text-warn"}>
            {data.files.duckdb_exists ? "ok" : "missing"}
          </span>
        </div>
        <div>
          lance: <span className={data.files.lance_exists ? "text-success" : "text-warn"}>
            {data.files.lance_exists ? "ok" : "missing"}
          </span>
        </div>
      </section>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Database;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="surface p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-fg-subtle">{label}</div>
        <Icon size={14} strokeWidth={1.5} className="text-fg-subtle" />
      </div>
      <div className="mt-2 font-mono text-2xl tabular-nums tracking-tight">
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-fg-subtle truncate">{sub}</div>}
    </div>
  );
}
